import React, { useMemo } from 'react';

import { IconName } from '@fortawesome/fontawesome-svg-core';
import { OneWayInPublicStatisticsDto, ServiceRecommendationCount } from 'src/api/v2';
import useOwiStatistics from '../../hooks/use-owi-statistics';
import { TFunction, useTranslation } from 'react-i18next';
import ValueField from 'src/components/ValueField/ValueField';
import styles from './OwiStatistics.module.scss';
import { Heading, Skeleton } from '@innovationnorway/cl-react';

type SimpleStat = {
  icon: IconName;
  label: string;
  value: number;
};

function getBaseStats(
  t: TFunction<'oneWayIn', undefined>,
  stats?: OneWayInPublicStatisticsDto,
): {
  [Property in keyof Omit<
    OneWayInPublicStatisticsDto,
    'statusMap' | 'serviceDeliveryMap'
  >]: SimpleStat | null;
} {
  if (!stats)
    return {
      numDeliveredServices: null,
      numForwarded: null,
      numOpportunities: null,
      numReceivedCorrect: null,
    };
  return {
    numDeliveredServices: {
      icon: 'hand-holding-heart',
      label: t('statistics.numDeliveredServices'),
      value: stats.numDeliveredServices || 0,
    },
    numForwarded: {
      icon: 'paper-plane',
      label: t('statistics.numOptyForwarded'),
      value: stats.numForwarded || 0,
    },
    numOpportunities: {
      icon: 'envelope',
      label: t('statistics.numOptyReceived'),
      value: stats.numOpportunities || 0,
    },
    numReceivedCorrect: {
      icon: 'box-check',
      label: t('statistics.numOptyReceivedCorrectly'),
      value: stats.numReceivedCorrect || 0,
    },
  };
}

interface ServiceDeliveryBarChartProps {
  data?: { [key: string]: ServiceRecommendationCount };
}

export const ServiceDeliveryBarChart = ({ data }: ServiceDeliveryBarChartProps) => {
  const { t } = useTranslation('oneWayIn');
  if (!data) return null;

  const maxValue = Math.max(...Object.values(data).flatMap((count) => [count.delivered, count.recommended]));
  const maxWidth = 200; // Maximum width for the bars in pixels

  return (
    <div className="stack">
      <Heading level={3}>{t('statistics.serviceDeliveryMap')}</Heading>

      <div className="flex gap--3">
        <div className="flex gap--1">
          <div className={`${styles.legendSquare} ${styles.recommended}`}></div>
          <span className="font-size--small">{t('statistics.serviceDeliveryMap.recommended')}</span>
        </div>
        <div className="flex gap--1">
          <div className={`${styles.legendSquare} ${styles.delivered}`}></div>
          <span className="font-size--small">{t('statistics.serviceDeliveryMap.delivered')}</span>
        </div>
      </div>

      <div>
        {Object.entries(data).map(([serviceId, count]) => (
          <div key={serviceId} className={styles.barChartRow}>
            <div className={styles.serviceName}>{count.serviceName}</div>

            <div className={styles.barsContainer}>
              <figure
                className={`${styles.bar} ${styles.recommended}`}
                style={{ width: `${(count.recommended / maxValue) * maxWidth}px` }}
              >
                <span className={styles.barNumber}>{count.recommended}</span>
              </figure>

              <figure
                className={`${styles.bar} ${styles.delivered}`}
                style={{ width: `${(count.delivered / maxValue) * maxWidth}px` }}
              >
                <span className={styles.barNumber}>{count.delivered}</span>
              </figure>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const OwiStatistics: React.FC = () => {
  const stats = useOwiStatistics();
  const { t: tOneWayIn } = useTranslation('oneWayIn');
  const { t: tCommon } = useTranslation('common');
  const baseStats = getBaseStats(tOneWayIn, stats.data);

  const recommendedServicesCount = useMemo(() => {
    return Object.entries(stats.data?.serviceDeliveryMap || [])
      .map((array) => array[1].recommended)
      .reduce((entry, acc) => (acc += entry), 0);
  }, [stats.data?.serviceDeliveryMap]);

  return (
    <section className="stack box background-color--white borderless flex-grow--1 flex-basis--40">
      <Heading level={2}>{tCommon('statistics')}</Heading>
      <div className="flex gap--4">
        {stats.data ? (
          <ValueField
            value={<span className="statistic green">{recommendedServicesCount}</span>}
            label={tOneWayIn('frontpage.statistics.recommended.total')}
          />
        ) : (
          <Skeleton width={250} height={150} />
        )}
        {baseStats.numDeliveredServices ? (
          <ValueField
            value={<span className="statistic yellow">{baseStats.numDeliveredServices?.value}</span>}
            label={baseStats.numDeliveredServices?.label}
          />
        ) : (
          <Skeleton width={250} height={150} />
        )}
      </div>
    </section>
  );
};

export default OwiStatistics;
